'use client'

import { tw } from 'utils/classnames'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { useTranslations } from 'next-intl'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { IconCheckCircle } from '@repo/design-system-cf/components/icons'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'
import { Button } from '@repo/design-system-cf/components/button'
import { LFS_URL } from '@repo/web-utils/env'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { transformImage } from '../../utils/image'

import { BannerContainer } from './container'

export interface Props {
  className?: string
  banner: Omit<
    NonNullable<SiteDataQuery['siteData']['studioAiYearlyTrialBanner']>,
    '__typename' | 'type'
  > & {
    type: string
  }
}

const image = `${LFS_URL}/web/features/banners/studio-ai/v2-image.png`
const image_md = `${LFS_URL}/web/features/banners/studio-ai/v2-md-image.png`

export function StudioAiYearlyTrial({ banner }: { banner: Props['banner'] }) {
  const t = useTranslations('cf_banners')

  const { features, headline, buttonText } = banner

  function onButtonClick() {
    sendAmplitudeEvent(AmplitudeEvents.CLICKED_BANNER_ABOVE_NAV, {
      banner_above_nav: banner.bannerAboveNav,
      banner_above_nav_state: banner.bannerAboveNavState,
    })
  }

  const isDailyGiftsPage =
    typeof window !== 'undefined' && window.location.pathname.startsWith(AppRoutes.DAILY_GIFTS)

  const entryPoint = isDailyGiftsPage
    ? PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_DAILY_GIFTS
    : PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV

  return (
    <BannerContainer
      className="bg-cf-yellow-300"
      wrapperClassName="m-0 p-4"
      bannerType={banner.type}
    >
      <div className="relative flex w-full py-2 md:py-6">
        <div className="z-[1] flex grow flex-col md:shrink-0 md:gap-5">
          <div className="flex flex-col gap-3">
            <div
              className="md:text-18 text-16 text-cf-grey-1000 mb-[10px] text-center font-bold leading-[140%] tracking-[0.3px] md:text-left"
              dangerouslySetInnerHTML={{ __html: headline }}
            />
            {features ? (
              <ul className="hidden flex-col gap-1 md:flex">
                {features.map((feat, index) => {
                  return (
                    <li
                      key={index}
                      className={tw(
                        'text-12 text-cf-grey-800 flex items-center gap-2 leading-[18px]',
                        index > 0 && 'hidden md:flex',
                      )}
                    >
                      <IconCheckCircle className="size-4 text-[#0B2304]" />
                      <span dangerouslySetInnerHTML={{ __html: feat }} />
                    </li>
                  )
                })}
              </ul>
            ) : null}
          </div>
          <div className="flex flex-col items-center gap-1 md:flex-row">
            <Button asChild variant="primary-green-bright" size="large">
              <StartFreeTrialButton
                subscriptionType="studio-ai"
                onClick={onButtonClick}
                label={buttonText}
                entryPoint={entryPoint}
              />
            </Button>
            <p className="text-12 text-cf-grey-800 mt-2 min-w-[134px] text-center md:mt-0 md:block">
              {t('cancel_anytime')}
            </p>
          </div>
        </div>
        <picture className="absolute inset-0 hidden lg:block">
          <source
            media="(min-width: 768px)"
            srcSet={transformImage(image).desktop}
            type="image/webp"
          />
          <source
            media="(max-width: 767px)"
            srcSet={transformImage(image).mobile}
            type="image/webp"
          />
          <img
            src={transformImage(image).mobile}
            alt="banner image"
            className="absolute -top-[10px] right-0 h-[calc(100%+20px)]"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        <picture className="absolute inset-0 hidden md:block lg:hidden">
          <source
            media="(min-width: 768px)"
            srcSet={transformImage(image_md).desktop}
            type="image/webp"
          />
          <source
            media="(max-width: 767px)"
            srcSet={transformImage(image_md).mobile}
            type="image/webp"
          />
          <img
            src={transformImage(image_md).mobile}
            alt="banner image"
            className="absolute -top-[10px] right-0 h-[calc(100%+20px)] rotate-[-11.8deg] rounded-[28px] bg-[#FCFCFC]"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </picture>
      </div>
    </BannerContainer>
  )
}
